import { resolveUnitModelLabel } from 'unitData/options/unitLabels';
import { findOption } from 'unitData/options/optionResolution';
import type { SimulationResultInterface } from 'store/simulation/types';
import type { UnitInfoDataInterface } from 'store/unitInfoData';
import { canShowReturnFlow } from 'unitData/Mobile/HSI/hsiData';
import { UnitTypes } from 'unitData/types';
import {
  conversionData,
  CONVERSIONS,
  convertToCurrentMetric,
  getMetricUnit,
} from 'util/conversions';
import type {
  PortOutputFlowInterface,
  UnitInfoMobileConeInterface,
  UnitInfoMobileHSIInterface,
  UnitInfoMobileJawAndConeInterface,
  UnitInfoMobileJawInterface,
  UnitInfoMobileScreenInterface,
  UnitInfoMobileVSIInterface,
  UnitInterface,
} from '../../store/units/types';
import { addNotesTexts } from './notesTexts';
import type { UnitInfoTextInterface } from './types';
import {
  addValue,
  configuration,
  getMetricType,
  loadDisplay,
  loadDisplayMobiles,
  mobileUnitName,
  modelName,
  toMetricCapacity,
  toMetricMM,
  toMetricRoundConv,
} from './utils';

export const getMobileJawTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
): UnitInfoTextInterface[] => {
  const unitInfo = unit.unitInfo as UnitInfoMobileJawInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);

  const { displayModelName, displayFeederModel } = unitInfo.display;

  const showFeedScreen = unitInfo.modelName.includes('+');
  const showFreeFallScreen =
    unitInfo.modelName === 'UJ440IVGFUS' ||
    unitInfo.modelName === 'UJ443EVGFUS' ||
    (unitInfo.modelName === 'UJ473' && unitInfo.configuration.configKey === 2);
  const showFreeFallScreen3D =
    unitInfo.modelName === 'UJ473' && unitInfo.configuration.configKey < 2;
  const isUJ473 = unitInfo.modelName === 'UJ473';
  const isUJ473Conf4 =
    unitInfo.modelName === 'UJ473' && unitInfo.configuration.configKey === 3;
  const showHangingScreen =
    (unitInfo.modelName === 'UJ440IVGF' ||
      unitInfo.modelName === 'UJ440IVGFUS') &&
    (unitInfo.configuration.configKey === 2 ||
      unitInfo.configuration.configKey === 3);
  const modelName = resolveUnitModelLabel(
    unit.type,
    unitInfo.modelName,
    unitInfoData,
  );
  const feederModel = `Feeder: ${
    showFeedScreen
      ? unitInfo.FEEDER_SCREEN1?.modelName.displayName
      : unitInfo.GRIZZLY_FEEDER1.modelName.displayName
  }`;
  const grizzlySeparation = isUJ473Conf4
    ? unitInfo.GRIZZLY_FEEDER2?.decks[0].separationSize
    : unitInfo.GRIZZLY_FEEDER1.decks[0].separationSize;
  const grizzlySeparation2 = isUJ473Conf4
    ? unitInfo.GRIZZLY_FEEDER2?.decks[1].separationSize
    : unitInfo.GRIZZLY_FEEDER1.decks[1].separationSize;
  const showGrizzlySeparation2 =
    (unitInfo.modelName.charAt(0) === 'U' &&
      unitInfo.modelName !== 'UJ440IVGFUS' &&
      unitInfo.modelName !== 'UJ443EVGFUS' &&
      unitInfo.modelName !== 'UJ473') ||
    (unitInfo.modelName === 'UJ473' && unitInfo.configuration.configKey === 3);
  const metricType = getMetricType();

  mobileUnitName(unitInfo, 'Mobile jaw', textArray);
  add(modelName, displayModelName, true);
  configuration(unitInfo, textArray);
  if (!isUJ473) add(feederModel, displayFeederModel);

  if (unitInfo.display.displayGrizzlySeparationSize && grizzlySeparation) {
    textArray.push({
      text: `Feeder separation: ${convertToCurrentMetric({
        value: grizzlySeparation,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
        decimals: 0,
      })} ${
        showGrizzlySeparation2
          ? `/ ${convertToCurrentMetric({
              value: grizzlySeparation2,
              metricType: metricType,
              conversionType: CONVERSIONS.MILLIMETER,
              decimals: 0,
            })}`
          : ''
      } ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
    });
  }

  const screenSep1 = unitInfo.FEEDER_SCREEN1?.decks[0].separationSize;
  const screenSep2 = unitInfo.FEEDER_SCREEN1?.decks[1].separationSize;

  if (
    unitInfo.display.displayScreenSeparationSize &&
    typeof screenSep1 === 'number' &&
    typeof screenSep2 === 'number' &&
    showFeedScreen
  ) {
    textArray.push({
      text: `Screen sep: ${convertToCurrentMetric({
        value: screenSep1,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })}/${convertToCurrentMetric({
        value: screenSep2,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
    });
  }

  if (unitInfo.display.displayCrusherModel) {
    textArray.push({
      text: `Crusher: ${unitInfo.JAW1.modelName.displayName}`,
    });
  }
  if (unitInfo.display.displayCrusherCss) {
    textArray.push({
      text: `CSS: ${convertToCurrentMetric({
        value: unitInfo.JAW1.css,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETERNOROUNDCONV,
      })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
    });
  }

  const FFSep1 = unitInfo.FREEFALL_SCREEN1?.decks[0].separationSize;
  const FFSep2 = unitInfo.FREEFALL_SCREEN1?.decks[1].separationSize;
  const FFSep3 = unitInfo.FREEFALL_SCREEN1?.decks[2].separationSize;
  if (
    unitInfo.display.displayFFSeparationSize &&
    typeof FFSep1 === 'number' &&
    typeof FFSep2 === 'number' &&
    showFreeFallScreen
  ) {
    textArray.push({
      text: `Free fall screen sep: ${convertToCurrentMetric({
        value: FFSep1,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })}/${convertToCurrentMetric({
        value: FFSep2,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
    });
  } else if (
    unitInfo.display.displayFFSeparationSize &&
    typeof FFSep1 === 'number' &&
    typeof FFSep2 === 'number' &&
    typeof FFSep3 === 'number' &&
    showFreeFallScreen3D
  ) {
    textArray.push({
      text: `Free fall screen sep: ${convertToCurrentMetric({
        value: FFSep1,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })}/${convertToCurrentMetric({
        value: FFSep2,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })}/${convertToCurrentMetric({
        value: FFSep3,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
    });
  }

  const HSSep = unitInfo.SCREEN1?.decks[0].separationSize;
  if (
    unitInfo.display.displayHSSeparationSize &&
    typeof HSSep === 'number' &&
    showHangingScreen
  ) {
    textArray.push({
      text: `Hanging screen sep: ${convertToCurrentMetric({
        value: HSSep,
        metricType: metricType,
        conversionType: CONVERSIONS.MILLIMETER,
      })} ${getMetricUnit(metricType, CONVERSIONS.MILLIMETER)}`,
    });
  }

  loadDisplay(unitInfo, load, textArray);

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getMobileScreenTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  outputFlow?: PortOutputFlowInterface,
  type?: string,
): UnitInfoTextInterface[] => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitData = unitInfoData.MOBILE_SCREEN;
  const unitInfo = unit.unitInfo as UnitInfoMobileScreenInterface;
  const load = simulationResult?.simulationUnitInfo?.load;

  const {
    displayModelName,
    displayGrizzlySeparation,
    displayFeederModel,
    displayNFScreenModel,
    displayScreenModel,
    displayScreenInclination,
    displayScreenSeparation,
    displayScreeningElement,
  } = unitInfo.display;

  const isWheeled = type === UnitTypes.MOBILE_WHEELED_SCREEN;
  const isQA451 = unitInfo.modelName.includes('QA451');
  const isQA452 = unitInfo.modelName.includes('QA452');
  const isUF373UF473 =
    unitInfo.modelName === 'UF373' || unitInfo.modelName === 'UF473';
  const isUF320 = unitInfo.modelName.includes('UF320');

  const showFeederScreenGrizzly =
    isWheeled && unitInfo.configuration.configKey <= 3;

  const displayName = `${unitData[
    unitInfo.modelName
  ].configurations.displayName.replace('(Discontinued)', '')}`;

  const configurationOption = findOption(
    unitData[unitInfo.modelName].configurations.configurationOptions,
    unitInfo.configuration.configKey,
  );
  const feederModel = `Feeder model: ${configurationOption?.feederText ?? ''}`;

  const nfScreenModel = `NF screen model: ${
    unitData[unitInfo.modelName].configurations.NFscreen
  }`;

  const grizzlyDeck1 = toMetricMM(
    'Grizzly deck 1 separation:',
    unitInfo.GENERIC_SCREEN1.decks[0].separationSize,
  );

  const deck1Separation = toMetricMM(
    'Deck 1 separation:',
    unitInfo.GENERIC_SCREEN2.decks[0].separationSize,
  );
  const deck2Separation = toMetricMM(
    'Deck 2 separation:',
    unitInfo.GENERIC_SCREEN2.decks[1].separationSize,
  );

  const screenModel = `Screen model: ${unitInfo.CONVENTIONAL_SCREEN1.model}`;
  const sfScreenModel = `Screen model: ${
    unitData[unitInfo.modelName]?.configurations.SFscreenModel
  }`;
  const screenInclination = `Screen inclination: ${unitInfo.CONVENTIONAL_SCREEN1.inclination} deg`;
  const deck1Split = `Deck 1 split: ${unitInfo.SPLIT1.amountRight} %`;
  const deck2Split = `Deck 2 split: ${unitInfo.SPLIT2.amountRight} %`;
  const deck3Split = `Deck 3 split: ${unitInfo.SPLIT3.amountRight} %`;

  mobileUnitName(unitInfo, 'Mobile screen', textArray);
  add(displayName, displayModelName, true);
  configuration(unitInfo, textArray);
  add(feederModel, displayFeederModel);
  add(nfScreenModel, displayNFScreenModel);
  add(grizzlyDeck1, showFeederScreenGrizzly && displayGrizzlySeparation);
  add(deck1Separation, displayGrizzlySeparation);
  add(deck2Separation, displayGrizzlySeparation);
  add(screenModel, displayScreenModel && isUF373UF473);
  add(sfScreenModel, displayScreenModel && !isUF373UF473);
  add(screenInclination, displayScreenInclination);

  if (unitInfo.modelName === 'QE141') {
    Object.values(unitInfo.GENERIC_SCREEN1.decks).forEach((deck, index) => {
      const separation = toMetricMM(
        `Screen deck ${index + 1} separation: `,
        deck.separationSize,
      );
      add(separation, displayScreenSeparation);
    });
  } else {
    Object.values(unitInfo.CONVENTIONAL_SCREEN1.decks).forEach(
      (deck, index) => {
        if (
          !isWheeled &&
          !isUF320 &&
          index === 2 &&
          !(isQA451 || isQA452) &&
          !isUF373UF473
        ) {
          return null;
        }
        const separation = toMetricMM(
          `Screen deck ${index + 1} separation:`,
          deck.separationSize,
        );
        const screeningElement = `Screen deck ${index + 1} element: ${
          deck.screeningMediaId?.name
        }`;

        add(separation, displayScreenSeparation);
        add(screeningElement, displayScreeningElement);
      },
    );
  }

  add(deck1Split, unitInfo.display.displayScreenSplits);
  add(deck2Split, unitInfo.display.displayScreenSplits && isUF373UF473);
  add(deck3Split, unitInfo.display.displayScreenSplits && isUF373UF473);
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getMobileHsiTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
) => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoMobileHSIInterface;
  const unitData = unitInfoData.MOBILE_HSI;
  const load = simulationResult?.simulationUnitInfo?.load;
  const {
    displayCrusherSetting,
    displayFeederSeparation,
    displayCrusherFrequency,
    displayCrusherMotorPower,
    displayCrusherTipSpeed,
    displayPanFeederModel,
    displayScreenSeparation,
    displayReturnFlow,
  } = unitInfo.display;
  const metricType = getMetricType();
  const feederModel = `Feeder model: ${
    unitData[unitInfo.modelName].configurations.SCREEN1.model.displayName
  }`;
  const crusherMode = `Crusher model: ${unitInfo.HSI1.modelName}`;
  const setting = toMetricRoundConv('Setting:', unitInfo.HSI1.setting);
  const motorFrequency = `Motor frequency: ${unitInfo.HSI1.motorFrequency} Hz`;
  const motorPower =
    metricType.value === 'IMPERIAL'
      ? `Motor power: ${(
          unitInfo.HSI1.motorPower * conversionData[CONVERSIONS.POWER].factor
        ).toFixed()} hp`
      : `Motor power: ${unitInfo.HSI1.motorPower} kW`;
  const tipSpeed = `Tip speed: ${unitInfo.HSI1.tipSpeed} m/s`;

  mobileUnitName(unitInfo, 'Mobile HSI', textArray);
  modelName(
    unitInfo,
    textArray,
    resolveUnitModelLabel(unit.type, unitInfo.modelName, unitInfoData),
  );
  configuration(unitInfo, textArray);
  add(feederModel, unitInfo.display.displayFeederModel);

  Object.values(unitInfo.SCREEN1.decks).forEach((deck, index) => {
    const sep = toMetricMM(
      `Feeder deck ${index + 1} separation:`,
      deck.separationSize,
    );
    add(sep, displayFeederSeparation);
  });

  add(crusherMode, unitInfo.display.displayCrusherModel);
  add(setting, displayCrusherSetting);
  add(motorFrequency, displayCrusherFrequency);
  add(motorPower, displayCrusherMotorPower);
  add(tipSpeed, displayCrusherTipSpeed);

  if (unitInfo.configuration.configKey > 1) {
    const panFeederModel = `Pan feeder model: ${
      unitData[unitInfo.modelName].configurations.PANFEEDER1?.model.displayName
    }`;
    add(panFeederModel, displayPanFeederModel);
  }

  if (unitInfo.configuration.configKey > 2) {
    Object.values(unitInfo.SCREEN2.decks).forEach((deck, index) => {
      if (index !== 1 || unitInfo.modelName !== 'QI341') {
        const sep = toMetricMM(
          `Screen deck ${index + 1} separation:`,
          deck.separationSize,
        );
        add(sep, displayScreenSeparation);
      }
    });
  }

  const simulationUnitInfo = simulationResult?.simulationUnitInfo as any;
  const closedCircuitFlowRate = simulationUnitInfo?.closedCircuitFlowRate ?? -1;
  const closedCircuitLowerFractionSize =
    simulationUnitInfo?.closedCircuitLowerFractionSize ?? -1;
  const closedCircuitUpperFractionSize =
    simulationUnitInfo?.closedCircuitUpperFractionSize ?? -1;

  if (
    canShowReturnFlow(unitInfo) &&
    displayReturnFlow &&
    closedCircuitFlowRate >= 0 &&
    closedCircuitLowerFractionSize >= 0 &&
    closedCircuitUpperFractionSize >= 0
  ) {
    const returnFlowText = toMetricCapacity(
      'Return flow to crusher: ',
      closedCircuitFlowRate,
      { decimals: 0 },
    );
    const sizeText = `,${toMetricMM('', closedCircuitLowerFractionSize, {
      hideUnit: true,
    })}-${toMetricMM('', closedCircuitUpperFractionSize)}`;
    add(returnFlowText + sizeText, true);
  }

  loadDisplay(unitInfo, load, textArray, true);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getMobileJawAndConeTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
) => {
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitData = unitInfoData.MOBILE_JAW_AND_CONE;
  const unitInfo = unit.unitInfo as UnitInfoMobileJawAndConeInterface;
  const {
    displayJawCrusherModel,
    displayJawCrusherCss,
    displayConeCrusherModel,
    displayConeCrusherThrow,
    displayConeCrusherCss,
    displayFeederModel,
    displayFeederSepSize,
    displayScreenModel,
    displayScreenSepSize,
  } = unitInfo.display;
  const load = simulationResult?.simulationUnitInfo?.load;
  const jawCrusherModel = `Jaw crusher model: ${unitInfo.JAW1.modelName}`;
  const jawCrusherCss = toMetricRoundConv(
    'Jaw crusher CSS:',
    unitInfo.JAW1.css,
  );
  const coneCrusherModel = `Cone crusher model: ${unitInfo.CONE1.modelName}`;
  const coneCrusherThrow = `Cone crusher throw: ${unitInfo.CONE1.unitThrow} mm`;
  const coneCrusherCss = toMetricRoundConv(
    'Cone crusher CSS:',
    unitInfo.CONE1.css,
  );
  const feederModel = `Feeder model: ${unitInfo.GRIZZLY_FEEDER1.modelName}`;
  const grizzlySep = toMetricMM(
    'Grizzly sep.:',
    unitInfo.GRIZZLY_FEEDER1.decks[0].separationSize,
  );
  const feederSep1 = toMetricMM(
    'Feeder sep. deck 1:',
    unitInfo.GRIZZLY_FEEDER1.decks[1].separationSize,
  );
  const screenModel = `Screen model: ${
    unitData[unitInfo.modelName].configurations.grizzlyModelName
  }`;

  const screenSep1 = toMetricMM(
    'Screen sep. deck 1:',
    unitInfo.CONVENTIONAL_SCREEN1.decks[0].separationSize,
  );
  const screenSep2 = toMetricMM(
    'Screen sep. deck 2:',
    unitInfo.CONVENTIONAL_SCREEN1.decks[1].separationSize,
  );
  const screenSep3 = toMetricMM(
    'Screen sep. deck 3:',
    unitInfo.CONVENTIONAL_SCREEN2.decks[0].separationSize,
  );
  const screenSep4 = toMetricMM(
    'Screen sep. deck 4:',
    unitInfo.CONVENTIONAL_SCREEN2.decks[1].separationSize,
  );

  mobileUnitName(unitInfo, 'Mobile jaw and cone', textArray);
  modelName(
    unitInfo,
    textArray,
    resolveUnitModelLabel(unit.type, unitInfo.modelName, unitInfoData),
  );
  configuration(unitInfo, textArray);
  add(jawCrusherModel, displayJawCrusherModel);
  add(jawCrusherCss, displayJawCrusherCss);
  add(coneCrusherModel, displayConeCrusherModel);
  add(coneCrusherThrow, displayConeCrusherThrow);
  add(coneCrusherCss, displayConeCrusherCss);
  add(feederModel, displayFeederModel);
  add(grizzlySep, displayFeederSepSize);
  add(feederSep1, displayFeederSepSize);
  add(screenModel, displayScreenModel);

  add(screenSep1, displayScreenSepSize);
  add(screenSep2, displayScreenSepSize);
  add(screenSep3, displayScreenSepSize);
  add(
    screenSep4,
    displayScreenSepSize && unitInfo.configuration.configKey === 0,
  );
  loadDisplay(unitInfo, load, textArray);
  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getMobileVsiTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
) => {
  const unitData = unitInfoData.MOBILE_VSI;
  const unitInfo = unit.unitInfo as UnitInfoMobileVSIInterface;
  const load = simulationResult?.simulationUnitInfo?.load;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);

  const {
    displayCrusherModel,
    displayCrusherTipSpeed,
    displayFeederModel,
    displayFeederSeparation,
    displayNFScreenModel,
    displayGrizzlySeparation,
    displayScreenModel,
    displayScreenSeparation,
  } = unitInfo.display;

  const showFeederScreen = unitInfo.modelName === 'UV320E_FLEX';
  const isUV320E_DIRECT = unitInfo.modelName === 'UV320E_DIRECT';
  const isUV210 = unitInfo.modelName === 'UV210';
  const crusherModelName = `Crusher model: ${unitInfo.VSI1.modelName}`;
  const tipSpeed = `Tip speed: ${unitInfo.VSI1.tipSpeed} m/s`;
  const feederModel = `Feeder model: ${
    unitData[unitInfo.modelName].configurations.PAN_FEEDER1?.model.displayName
  }`;
  const feederSeparation = toMetricMM(
    'Feeder separation:',
    unitData[unitInfo.modelName]?.configurations?.SCREEN1?.decks[0]
      .defaultSeparationSize,
  );
  const nfScreenModel = `NF screen model: ${
    unitData[unitInfo.modelName]?.configurations?.SCREEN2?.model.displayName
  }`;
  const screen =
    showFeederScreen || isUV320E_DIRECT
      ? 'FREEFALL_SCREEN1'
      : 'CONVENTIONAL_SCREEN1';

  const screenModel = `Screen model: ${
    unitData[unitInfo.modelName]?.configurations?.[screen]?.model.displayName
  }`;
  const screenDecks = Object.values(
    unitInfo[screen === 'FREEFALL_SCREEN1' ? 'FREEFALL_SCREEN1' : 'SCREEN1']
      .decks,
  );

  mobileUnitName(unitInfo, 'Mobile VSI', textArray);
  modelName(
    unitInfo,
    textArray,
    resolveUnitModelLabel(unit.type, unitInfo.modelName, unitInfoData),
  );
  configuration(unitInfo, textArray);
  add(crusherModelName, displayCrusherModel);
  add(tipSpeed, displayCrusherTipSpeed);
  add(feederModel, displayFeederModel);
  add(feederSeparation, displayFeederSeparation);
  add(nfScreenModel, displayNFScreenModel);

  Object.values(unitInfo.SCREEN2.decks).forEach((deck, index) => {
    const sep = toMetricMM(
      `Screen deck ${index + 1} separation:`,
      deck.separationSize,
    );
    add(sep, displayGrizzlySeparation);
  });

  add(screenModel, displayScreenModel);

  Object.values(screenDecks).forEach((deck, index) => {
    if (!(isUV210 && index > 2)) {
      const sep = toMetricMM(
        `Screen deck ${index + 1} separation:`,
        deck.separationSize,
      );
      add(sep, displayScreenSeparation);
    }
  });

  loadDisplay(unitInfo, load, textArray);

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};

export const getMobileConeTexts = (
  unit: UnitInterface,
  simulationResult: SimulationResultInterface | undefined,
  unitInfoData: UnitInfoDataInterface,
  _outputFlow?: PortOutputFlowInterface,
) => {
  const unitData = unitInfoData.MOBILE_CONE;
  const textArray: UnitInfoTextInterface[] = [];
  const add = addValue(textArray);
  const unitInfo = unit.unitInfo as UnitInfoMobileConeInterface;

  const {
    displayModelName,
    displayFeederModel,
    displayFeederScreenModel,
    displayFeederScreenSeparation,
    displayCrusherModel,
    displayCrusherConcave,
    displayCrusherMantle,
    displayCrusherThrow,
    displayCrusherCss,
    displayScreenModel,
    displayScreenSeparation,
    displayReturnFlow,
  } = unitInfo.display;

  const feederLoad = simulationResult?.simulationUnitInfo?.feederLoad;
  const crusherLoad = simulationResult?.simulationUnitInfo?.crusherLoad;
  const screenLoad = simulationResult?.simulationUnitInfo?.screenLoad;
  const returnFlow =
    simulationResult?.simulationUnitInfo?.closedCircuitFlowRate;
  const type = unit.type;
  const modelName = resolveUnitModelLabel(
    unit.type,
    unitInfo.modelName,
    unitInfoData,
  );

  mobileUnitName(unitInfo, 'Mobile cone', textArray);
  add(modelName, displayModelName, true);

  const hasConfig =
    !unitInfo.modelName.includes('373') && !unitInfo.modelName.includes('573');

  if (hasConfig) {
    configuration(unitInfo, textArray);
  }

  const isWheeled = type === UnitTypes.MOBILE_WHEELED_CONE;
  const isWheeledFlex = unitInfo.modelName === 'UH320_Flex';
  const isWheeledUK = unitInfo.modelName === 'UK373';
  const isUS = unitInfo.modelName.includes('US');
  const isUH440I = unitInfo.modelName.includes('UH440i');
  const isQH441 = unitInfo.modelName.includes('QH441');
  const hasScreen =
    unitInfo.modelName !== 'UH373' &&
    unitInfo.modelName !== 'UH573' &&
    unitInfo.modelName !== 'US640E' &&
    unitInfo.modelName !== 'US373';
  const hasFeeder =
    unitInfo.modelName.includes('440E') || unitInfo.modelName === 'UH640';
  const showHangingScreen =
    (isUH440I || isQH441 || (isUS && !hasFeeder)) &&
    unitInfo.configuration.configKey !== 0;
  const {
    SCREEN1,
    hangingScreen,
    feederScreen: { feederScreenDecks, screenDecks },
    wheeledFlexScreen: {
      feederScreenDecks: feederScreenDecksFlex,
      decks: decksFlex,
    },
    wheeledScreenUK: { CONVENTIONAL_SCREEN1: CONVENTIONAL_SCREEN1_UK },
    wheeledScreen: { CONVENTIONAL_SCREEN1 },
  } = unitInfo;

  let feederName = undefined;
  if (isWheeledFlex) {
    feederName =
      unitData[unitInfo.modelName]?.configurations?.PAN_FEEDER1?.model
        .displayName;
  } else {
    feederName = unitInfo.feederScreen.feederModel;
  }
  if (displayFeederModel && (hasFeeder || isWheeledFlex)) {
    add(`Feeder model: ${feederName}`, true);
  }
  let feederScreenName = undefined;
  if (isWheeledFlex) {
    feederScreenName =
      unitData[unitInfo.modelName]?.configurations?.SCREEN2?.model.displayName;
  } else {
    feederScreenName = unitInfo.feederScreen.feederScreenModel;
  }
  if (displayFeederScreenModel && (hasFeeder || isWheeledFlex)) {
    add(`Feeder screen model: ${feederScreenName}`, true);
  }

  const getDeckText =
    (label: string) =>
    (decks: Record<number, { separationSize: number }>, index: number) => {
      return toMetricMM(`${label} ${index + 1}: `, decks[index].separationSize);
    };

  const feederDeckText = getDeckText('Sep. feeder deck');

  if (displayFeederScreenSeparation) {
    if (hasFeeder) {
      add(feederDeckText(feederScreenDecks.decks, 0), true);
      add(feederDeckText(feederScreenDecks.decks, 1), true);
      add(feederDeckText(feederScreenDecks.decks, 2), true);
    } else if (isWheeledFlex) {
      add(
        feederDeckText(feederScreenDecksFlex, 0),
        unitInfo.configuration.configKey < 2,
      );

      add(feederDeckText(feederScreenDecksFlex, 1), true);
      add(feederDeckText(feederScreenDecksFlex, 2), true);
    }
  }

  let modelText = '';
  if (displayCrusherModel) {
    modelText = unitInfo.CONE1.modelName + ' ';
  }
  if (displayCrusherConcave) {
    modelText = modelText + unitInfo.CONE1.concave + ' ';
  }
  if (displayCrusherMantle) {
    modelText = modelText + '(' + unitInfo.CONE1.mantle + ') ';
  }

  add(`Cone crusher model: ${modelText}`, !!modelText);
  add(
    `Cone crusher throw: ${unitInfo.CONE1.unitThrow} mm`,
    displayCrusherThrow,
  );
  add(
    toMetricRoundConv('Cone crusher CSS:', unitInfo.CONE1.css),
    displayCrusherCss,
  );

  let screenName = undefined;
  if (isWheeled) {
    if (isWheeledUK) {
      screenName = unitInfo.wheeledScreenUK.CONVENTIONAL_SCREEN1.model;
    } else if (isWheeledFlex) {
      screenName = unitInfo.wheeledFlexScreen.model;
    } else {
      screenName = unitInfo.wheeledScreen.CONVENTIONAL_SCREEN1.model;
    }
  } else if (hasFeeder) {
    screenName = unitInfo.feederScreen.screenModel;
  } else if (unitInfo.configuration.configKey !== 0) {
    screenName = unitInfo.SCREEN1.model;
  }

  const screenDeckText = getDeckText('Sep. screen deck');
  const hangingScreenDeckText = getDeckText('Sep. screen deck');

  add(
    `Screen model: ${screenName}`,
    displayScreenModel && hasScreen && !!screenName,
  );

  if (displayScreenSeparation && hasScreen) {
    if (hasFeeder) {
      add(screenDeckText(screenDecks.decks, 0), true);
      add(screenDeckText(screenDecks.decks, 1), true);
      add(screenDeckText(screenDecks.decks, 2), true);
    } else if (showHangingScreen && !isQH441) {
      add(hangingScreenDeckText(hangingScreen.decks, 0), true);
    } else if (isWheeledFlex) {
      add(screenDeckText(decksFlex, 0), true);
      add(screenDeckText(decksFlex, 1), true);
      add(screenDeckText(decksFlex, 2), true);
    } else if (isWheeledUK) {
      add(screenDeckText(CONVENTIONAL_SCREEN1_UK.decks, 0), true);
      add(screenDeckText(CONVENTIONAL_SCREEN1_UK.decks, 1), true);
      add(screenDeckText(CONVENTIONAL_SCREEN1_UK.decks, 2), true);
    } else if (isWheeled) {
      add(screenDeckText(CONVENTIONAL_SCREEN1.decks, 0), true);
      add(screenDeckText(CONVENTIONAL_SCREEN1.decks, 1), true);
      add(screenDeckText(CONVENTIONAL_SCREEN1.decks, 2), true);
      const showDeck3 =
        unitInfo.modelName === 'UH412' ||
        unitInfo.modelName === 'UH311' ||
        unitInfo.modelName === 'UH312';
      add(screenDeckText(CONVENTIONAL_SCREEN1.decks, 3), showDeck3);
    } else if (unitInfo.configuration.configKey !== 0) {
      add(
        screenDeckText(SCREEN1.decks, 0),
        unitInfo.configuration.configKey !== 0,
      );
      if (!isQH441) {
        add(
          screenDeckText(SCREEN1.decks, 1),
          unitInfo.configuration.configKey !== 0,
        );
      }
    }
  }

  if ((hasScreen || showHangingScreen) && returnFlow !== undefined) {
    if (returnFlow >= 0) {
      add(
        toMetricCapacity('Return flow to crusher: ', returnFlow, {
          decimals: 0,
        }),
        displayReturnFlow,
      );
    }
  }

  loadDisplayMobiles(unitInfo, feederLoad, crusherLoad, screenLoad, textArray);

  addNotesTexts({ unitInfo, textArray });

  return textArray;
};
